import { useEffect, useState } from "react";
import { FinancleCardPresentation } from "../../components/common/FinancleCardPresentation";
import { getDashboardBooking, getDashboardBookingsByBranchId } from "../../services/dashboardBooking";
import moment from "moment";
import { CircularProgress, Divider } from "@mui/material";
import { useTheme } from "../../components/hooks/useTheme";
import { CustomPieChart } from "../../components/charts/piechart/CustomPieChart";
import { CustomBarGraph } from "../../components/charts/barchart/CustomBarGraph";
import Cookies from "js-cookie";
import useTenant from "../../components/hooks/useTenant";


type Props = {
  branchId: number;
  startDate: string;
  endDate: string;
  dateType: string;
}

export const Bookings = ({ branchId, startDate, endDate, dateType }: Props) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { handleTenantError }: any = useTenant();
  const { themeStyles } = useTheme();
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [bookingData, setBookingData] = useState<any>(null);
  const [financleData, setFinancleData] = useState<any[]>([]);
  const [bookedForDay, setBookedForDay] = useState<any[]>([])
  const [bookedForStatus, setBookedForStatus] = useState<any[]>([])
  const [bookedForCountry, setBookedForCountry] = useState<any[]>([])
  const [bookedForState, setBookedForState] = useState<any[]>([])
  const [bookedForPerPickupLocation, setBookedForPerPickupLocation] = useState<any[]>([])

  useEffect(() => {
    setIsDataLoaded(false);
    if (branchId > 0) {
      getDashboardBookingsByBranchId(branchId, dateType, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), tenantId)
        .then(data => {
          setBookingData(data)
          setIsDataLoaded(true);
          handleTenantError(null);
        })
        .catch(error => handleTenantError(error))
    } else {
      getDashboardBooking(dateType, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), tenantId)
        .then(data => {
          setBookingData(data)
          setIsDataLoaded(true);
          handleTenantError(null);
        })
        .catch(error => handleTenantError(error))
    }


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, startDate, endDate])

  useEffect(() => {
    if (bookingData !== null) {
      const financleResults = [
        {
          label: "Revenue",
          symbol: "$",
          count: bookingData?.revenue,
          vsLastMonthPercentage: bookingData?.growthRevenueInPercentage,
        },
        {
          label: "Amount Paid",
          symbol: "$",
          count: bookingData?.amountPaid,
          vsLastMonthPercentage: bookingData?.growthAmountPaidInPercentage,
        },
        {
          label: "Outstanding Balance",
          count: bookingData?.outstandingBalance,
          symbol: "$",
          vsLastMonthPercentage: bookingData?.growthOutstandingBalanceInPercentage,
        },
        {
          label: "Reservations",
          count: bookingData?.reservations,
          vsLastMonthPercentage: bookingData?.growthReservationsInPercentage,
        },
        {
          label: "Reservation Average",
          symbol: "$",
          count: bookingData?.reservationAverage,
          vsLastMonthPercentage: bookingData?.growthReservationAverageInPercentage,
        },
        {
          label: "Reservation From Website",
          count: bookingData?.reservationFromWebSite,
          vsLastMonthPercentage: bookingData?.growthReservationFromWebSiteInPercentage,
        },
        {
          label: "Reservation With Extras",
          count: bookingData?.reservationWithAditionalCharges,
          vsLastMonthPercentage: bookingData?.growthReservationWithAditionalChargesInPercentage,
        },
        {
          label: "Average Rental Days",
          count: bookingData?.averageRentalDays,
          vsLastMonthPercentage: bookingData?.growthAverageRentalDaysInPercentage,
        },
      ]
      setFinancleData(financleResults);
    }

  }, [bookingData])

  useEffect(() => {
    if (bookingData !== null) {
      setBookedForDay([
        { name: 'Sunday', value: bookingData?.bookingsPerDay?.sunday },
        { name: 'Monday', value: bookingData?.bookingsPerDay?.monday },
        { name: 'Tuesday', value: bookingData?.bookingsPerDay?.tuesday },
        { name: 'Wednesday', value: bookingData?.bookingsPerDay?.wednesday },
        { name: 'Thursday', value: bookingData?.bookingsPerDay?.thursday },
        { name: 'Friday', value: bookingData?.bookingsPerDay?.friday },
        { name: 'Saturday', value: bookingData?.bookingsPerDay?.saturday }
      ]);
    }
  }, [bookingData])

  useEffect(() => {
    if (bookingData !== null) {
      setBookedForStatus([
        { name: 'No Show', value: bookingData?.bookingsPerStatus?.noShow },
        { name: 'Completed', value: bookingData?.bookingsPerStatus?.completed },
        { name: 'Rental', value: bookingData?.bookingsPerStatus?.rental },
        { name: 'Open', value: bookingData?.bookingsPerStatus?.open },
        { name: 'Cancelled Paid', value: bookingData?.bookingsPerStatus?.cancelledPaid },
        { name: 'Cancelled', value: bookingData?.bookingsPerStatus?.cancelled }
      ]);
    }
  }, [bookingData])

  useEffect(() => {
    if (bookingData !== null) {
      setBookedForCountry([
        { name: 'United State', value: bookingData?.bookingsPerCountry?.unitedState },
        { name: 'Canada', value: bookingData?.bookingsPerCountry?.canada },
        { name: 'Australia', value: bookingData?.bookingsPerCountry?.australia },
        { name: 'United Kingdom', value: bookingData?.bookingsPerCountry?.unitedKingdom },
        { name: 'Germany', value: bookingData?.bookingsPerCountry?.germany },
        { name: 'France', value: bookingData?.bookingsPerCountry?.france },
        { name: 'China', value: bookingData?.bookingsPerCountry?.china },
        { name: 'India', value: bookingData?.bookingsPerCountry?.india },
        { name: 'Argentina', value: bookingData?.bookingsPerCountry?.argentina },
        { name: 'Brazil', value: bookingData?.bookingsPerCountry?.brazil },
        { name: 'Uruguay', value: bookingData?.bookingsPerCountry?.uruguay },
        { name: 'Colombia', value: bookingData?.bookingsPerCountry?.colombia },
        { name: 'Chile', value: bookingData?.bookingsPerCountry?.chile },
        { name: 'Peru', value: bookingData?.bookingsPerCountry?.peru },
        { name: 'Ecuador', value: bookingData?.bookingsPerCountry?.ecuador },
        { name: 'Paraguay', value: bookingData?.bookingsPerCountry?.paraguay },
        { name: 'Venezuela', value: bookingData?.bookingsPerCountry?.venezuela },
        { name: 'Bolivia', value: bookingData?.bookingsPerCountry?.bolivia },
        { name: 'Guyana', value: bookingData?.bookingsPerCountry?.guyana },
        { name: 'Suriname', value: bookingData?.bookingsPerCountry?.suriname },
      ]);
    }
  }, [bookingData])

  useEffect(() => {
    if (bookingData !== null) {
      setBookedForState([
        { name: "Alabama", value: bookingData?.bookingsPerState?.alabama },
        { name: "Alaska", value: bookingData?.bookingsPerState?.alaska },
        { name: "Arizona", value: bookingData?.bookingsPerState?.arizona },
        { name: "Arkansas", value: bookingData?.bookingsPerState?.arkansas },
        { name: "California", value: bookingData?.bookingsPerState?.california },
        { name: "Colorado", value: bookingData?.bookingsPerState?.colorado },
        { name: "Connecticut", value: bookingData?.bookingsPerState?.connecticut },
        { name: "Delaware", value: bookingData?.bookingsPerState?.delaware },
        { name: "Florida", value: bookingData?.bookingsPerState?.florida },
        { name: "Georgia", value: bookingData?.bookingsPerState?.georgia },
        { name: "Hawaii", value: bookingData?.bookingsPerState?.hawaii },
        { name: "Idaho", value: bookingData?.bookingsPerState?.idaho },
        { name: "Illinois", value: bookingData?.bookingsPerState?.illinois },
        { name: "Indiana", value: bookingData?.bookingsPerState?.indiana },
        { name: "Iowa", value: bookingData?.bookingsPerState?.iowa },
        { name: "Kansas", value: bookingData?.bookingsPerState?.kansas },
        { name: "Kentucky", value: bookingData?.bookingsPerState?.kentucky },
        { name: "Louisiana", value: bookingData?.bookingsPerState?.louisiana },
        { name: "Maine", value: bookingData?.bookingsPerState?.maine },
        { name: "Maryland", value: bookingData?.bookingsPerState?.maryland },
        { name: "Massachusetts", value: bookingData?.bookingsPerState?.massachusetts },
        { name: "Michigan", value: bookingData?.bookingsPerState?.michigan },
        { name: "Mississippi", value: bookingData?.bookingsPerState?.mississippi },
        { name: "Missouri", value: bookingData?.bookingsPerState?.missouri },
        { name: "Montana", value: bookingData?.bookingsPerState?.montana },
        { name: "Nebraska", value: bookingData?.bookingsPerState?.nebraska },
        { name: "Nevada", value: bookingData?.bookingsPerState?.nevada },
        { name: "New Hampshire", value: bookingData?.bookingsPerState?.newHampshire },
        { name: "New Jersey", value: bookingData?.bookingsPerState?.newJersey },
        { name: "New Mexico", value: bookingData?.bookingsPerState?.newMexico },
        { name: "New York", value: bookingData?.bookingsPerState?.newYork },
        { name: "North Carolina", value: bookingData?.bookingsPerState?.northCarolina },
        { name: "North Dakota", value: bookingData?.bookingsPerState?.northDakota },
        { name: "Ohio", value: bookingData?.bookingsPerState?.ohio },
        { name: "Oklahoma", value: bookingData?.bookingsPerState?.oklahoma },
        { name: "Oregon", value: bookingData?.bookingsPerState?.oregon },
        { name: "Pennsylvania", value: bookingData?.bookingsPerState?.pennsylvania },
        { name: "Rhode Island", value: bookingData?.bookingsPerState?.rhodeIsland },
        { name: "South Carolina", value: bookingData?.bookingsPerState?.southCarolina },
        { name: "South Dakota", value: bookingData?.bookingsPerState?.southDakota },
        { name: "Tennessee", value: bookingData?.bookingsPerState?.tennessee },
        { name: "Texas", value: bookingData?.bookingsPerState?.texas },
        { name: "Utah", value: bookingData?.bookingsPerState?.utah },
        { name: "Vermont", value: bookingData?.bookingsPerState?.vermont },
        { name: "Virginia", value: bookingData?.bookingsPerState?.virginia },
        { name: "Washington", value: bookingData?.bookingsPerState?.washington },
        { name: "West Virginia", value: bookingData?.bookingsPerState?.westVirginia },
        { name: "Wisconsin", value: bookingData?.bookingsPerState?.wisconsin },
        { name: "Wyoming", value: bookingData?.bookingsPerState?.wyoming }
      ]);
    }
  }, [bookingData])

  useEffect(() => {
    if (bookingData !== null) {
      setBookedForPerPickupLocation(
        bookingData?.bookingsPerPickupLocations?.map((item: any) => ({
          name: item?.name,
          value: item?.count
        }))
      );
    }
  }, [bookingData])

  return (
    <>
      {
        !isDataLoaded ? (
          <div className="flex h-[300px] items-center justify-center">
            <CircularProgress />
          </div>
        ) : (
            <>
              <div className="w-full mt-8 mb-4 ml-[10px]">
                <FinancleCardPresentation data={financleData} dateType={dateType} />
              </div>

              <Divider className={`w-full ${themeStyles.divider.bg}`} />

              <div className="w-full flex flex-wrap">
                {
                  bookedForDay.length > 0 && (
                    <CustomPieChart
                      data={bookedForDay}
                      title="Bookings Per Day"
                      legendsFor="Bookings"
                    />

                  )
                }

                {
                  bookedForStatus.length > 0 && (
                    <CustomPieChart
                      data={bookedForStatus}
                      title="Bookings Per Status"
                      legendsFor="Bookings"
                    />
                  )
                }

                {
                  bookedForCountry.length > 0 && (
                    <CustomPieChart
                      data={bookedForCountry}
                      title="Bookings Per Country"
                      legendsFor="Bookings"
                    />
                  )
                }

                {
                  bookedForPerPickupLocation.length > 0 && (
                    <CustomPieChart
                      data={bookedForPerPickupLocation}
                      title="Bookings Per Pickup Location"
                      legendsFor="Bookings"
                    />
                  )
                }
              </div>

              <div className="w-full mt-6 pr-[12px]">
                {
                  bookedForState.length > 0 && (
                    <CustomBarGraph
                      title="Bookings Per State"
                      data={bookedForState} />
                  )
                }

              </div>
            </>
          )
      }
    </>
  )
}